import { isMobile } from '@/biz/tool';
import {
  ChevronStrokeIcon,
  LeftArrowIcon,
  TipsIcon
} from '@/components/common/icon';
import { Drawer, Tooltip } from 'aha-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FairRangeEstimateHint({
  showMobileChevron = false,
  useFairPriceCopy = false
}: {
  showMobileChevron?: boolean;
  useFairPriceCopy?: boolean;
}) {
  const { t } = useTranslation();
  const [showFairRangeDrawer, setShowFairRangeDrawer] = useState(false);
  const mobile = isMobile();
  const fairRangeTitle = useFairPriceCopy
    ? t('multi_platform.quote.fair_price_help', {
        defaultValue: 'How we estimate fair price range'
      })
    : t('price.fields.set_your_rate.fair_range_estimate_link', {
        defaultValue: 'How we estimate recommended price range'
      });
  const fairRangeContent = t('price.fields.set_your_rate.fair_range_tooltip', {
    defaultValue:
      "The recommended price range is the market rate for this deal. It's estimated from recent deals by similar creators, factoring in your audience, niche, channel performance (e.g., average views, follower counts), how well you fit this brand, and the brand’s budget constraints. Pricing within this range makes sure you're paid what your work is worth while keeping your chance of landing the collaboration as high as possible."
  });
  const fairRangeLink = (
    <div className="flex items-center gap-1">
      <TipsIcon />
      <div className="justify-start text-text-primary text-sm font-normal font-['DM_Sans'] leading-5">
        {fairRangeTitle}
      </div>
    </div>
  );

  if (mobile) {
    return (
      <>
        <button
          type="button"
          className={
            showMobileChevron
              ? 'flex w-full min-w-0 items-center justify-between gap-2 border-0 bg-transparent p-0 text-left'
              : 'cursor-pointer border-0 bg-transparent p-0 text-left'
          }
          onClick={() => setShowFairRangeDrawer(true)}
        >
          {fairRangeLink}
          {showMobileChevron ? (
            <span
              aria-hidden
              className="flex h-4 w-4 shrink-0 items-center justify-center"
            >
              <ChevronStrokeIcon className="h-[9.5px] w-[5.5px] text-[#A3A3A3]" />
            </span>
          ) : null}
        </button>
        <Drawer
          zIndex={10010}
          open={showFairRangeDrawer}
          onClose={() => setShowFairRangeDrawer(false)}
          placement="bottom"
          height="auto"
          closable={false}
          drawerStyle={{
            borderRadius: '16px 16px 0 0'
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div className="flex flex-col gap-4 px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label={t('common.modal.back_btn', {
                  defaultValue: 'Back'
                })}
                className="flex-shrink-0"
                onClick={() => setShowFairRangeDrawer(false)}
              >
                <LeftArrowIcon className="w-6 h-6" />
              </button>
              <div className="justify-start text-Colors-Text-text-primary-(900) text-lg font-semibold font-['Inter'] leading-7">
                {fairRangeTitle}
              </div>
            </div>
            <div className="self-stretch justify-start text-Colors-Text-text-primary-(900) text-lg font-normal font-['Inter'] leading-7">
              {fairRangeContent}
            </div>
          </div>
        </Drawer>
      </>
    );
  }

  return <Tooltip content={fairRangeContent}>{fairRangeLink}</Tooltip>;
}
